/**
 * When adding a connected mission from a host that already feeds into a
 * milestone, decide whether that milestone is on the host's output or on a
 * unique child's input, and apply before/after placement.
 */

import { hasAnyConditions, getDependencyTaskId, getDependencyBranch } from '../../shared/ConditionsDropdown/utils/conditionHelpers';
import { buildLogicalEdgePairs } from './edgePaths/edgeGeneration';
import { buildMilestonesByHostMap, getMilestoneAnchor, isMilestoneNode } from './milestoneUtils';

/**
 * @returns {{
 *   kind: 'output'|'input',
 *   milestone: object,
 *   outputMilestones?: object[],
 *   inputMilestones?: object[],
 *   childId?: string,
 *   branch?: string|null,
 * } | null}
 */
export function getDirectFeedMilestone(sourceTaskId, tasks) {
  if (!sourceTaskId) return null;

  const { inputByHost, outputByHost } = buildMilestonesByHostMap(tasks);
  const outputMilestones = outputByHost.get(sourceTaskId) || [];
  if (outputMilestones.length > 0) {
    return {
      kind: 'output',
      milestone: outputMilestones[0],
      outputMilestones,
    };
  }

  const outgoing = buildLogicalEdgePairs(tasks).filter((pair) => pair.from === sourceTaskId);
  if (outgoing.length !== 1) return null;

  const childId = outgoing[0].to;
  const inputMilestones = inputByHost.get(childId) || [];
  if (inputMilestones.length === 0) return null;

  return {
    kind: 'input',
    milestone: inputMilestones[0],
    inputMilestones,
    childId,
    branch: outgoing[0].branch || null,
  };
}

/**
 * Clicking (+) on a milestone inserts the new mission after that marker.
 * Output markers stay on the host; later stacked markers move onto the new mission.
 * Input markers sit before their host, so the new mission is inserted ahead of the host.
 */
export function getPlacementAfterMilestone(milestoneId, tasks) {
  const milestone = (tasks || []).find((t) => t.id === milestoneId);
  if (!isMilestoneNode(milestone)) return null;

  const anchor = getMilestoneAnchor(milestone);
  if (!anchor) return null;

  const { inputByHost, outputByHost } = buildMilestonesByHostMap(tasks);

  if (anchor.side === 'output') {
    const outputMilestones = outputByHost.get(anchor.hostTaskId) || [];
    return {
      hostTaskId: anchor.hostTaskId,
      placement: 'after',
      afterMilestoneId: milestoneId,
      insertBeforeHost: false,
      feed: {
        kind: 'output',
        milestone,
        outputMilestones,
      },
    };
  }

  const inputMilestones = inputByHost.get(anchor.hostTaskId) || [];
  return {
    hostTaskId: anchor.hostTaskId,
    placement: 'after',
    afterMilestoneId: milestoneId,
    insertBeforeHost: true,
    feed: {
      kind: 'input',
      milestone,
      inputMilestones,
      childId: anchor.hostTaskId,
    },
  };
}

function depMatchesSource(depId, depBranch, sourceTaskId, branch) {
  if (depId !== sourceTaskId) return false;
  if (branch && depBranch && depBranch !== branch) return false;
  return true;
}

function rewireDependsOn(deps, sourceTaskId, newTaskId, branch) {
  let changed = false;
  const next = deps.map((dep) => {
    const depId = getDependencyTaskId(dep);
    const depBranch = getDependencyBranch(dep);
    if (!depMatchesSource(depId, depBranch, sourceTaskId, branch)) return dep;
    changed = true;
    return newTaskId;
  });
  return changed ? next : deps;
}

function rewireConditions(unlockConditions, sourceTaskId, newTaskId, branch) {
  if (!unlockConditions) return unlockConditions;

  if (unlockConditions.version === 2 && Array.isArray(unlockConditions.blocks)) {
    let changed = false;
    const blocks = unlockConditions.blocks.map((block) => ({
      ...block,
      conditions: (block.conditions || []).map((condition) => {
        if (!depMatchesSource(condition.taskId, condition.branch, sourceTaskId, branch)) {
          return condition;
        }
        changed = true;
        return { ...condition, taskId: newTaskId, branch: null };
      }),
    }));
    return changed ? { ...unlockConditions, blocks } : unlockConditions;
  }

  if (Array.isArray(unlockConditions)) {
    let changed = false;
    const next = unlockConditions.map((condition) => {
      if (!depMatchesSource(condition.taskId, condition.branch, sourceTaskId, branch)) {
        return condition;
      }
      changed = true;
      return { ...condition, taskId: newTaskId, branch: undefined };
    });
    return changed ? next : unlockConditions;
  }

  return unlockConditions;
}

function rewireTaskDependency(task, sourceTaskId, newTaskId, branch) {
  if (hasAnyConditions(task.unlockConditions)) {
    const unlockConditions = rewireConditions(task.unlockConditions, sourceTaskId, newTaskId, branch);
    return unlockConditions === task.unlockConditions ? task : { ...task, unlockConditions };
  }

  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
  const dependsOn = rewireDependsOn(deps, sourceTaskId, newTaskId, branch);
  return dependsOn === deps ? task : { ...task, dependsOn };
}

function setTaskDependsOnNew(task, newTaskId) {
  if (hasAnyConditions(task.unlockConditions)) {
    return {
      ...task,
      unlockConditions: {
        version: 2,
        blocks: [{
          conditions: [{ taskId: newTaskId, branch: null }],
          innerOperator: 'AND',
          outerOperator: null,
        }],
      },
    };
  }
  return { ...task, dependsOn: [newTaskId] };
}

function idsAfterMilestone(stack, afterMilestoneId, inclusive) {
  const index = stack.findIndex((m) => m.id === afterMilestoneId);
  if (index < 0) return new Set();
  const start = inclusive ? index : index + 1;
  return new Set(stack.slice(start).map((m) => m.id));
}

/**
 * Mutate the just-added connected task's graph so it sits before or after the
 * milestone the source already feeds into.
 */
export function applyConnectedTaskMilestonePlacement(tasks, {
  newTaskId,
  sourceTaskId,
  branch,
  placement,
  feed,
  afterMilestoneId = null,
}) {
  if (!feed || (placement !== 'before' && placement !== 'after')) return tasks;

  const outputStack = feed.outputMilestones || [];
  const inputStack = feed.inputMilestones || [];

  const laterOutputIds = afterMilestoneId && feed.kind === 'output'
    ? idsAfterMilestone(outputStack, afterMilestoneId, false)
    : new Set();
  const inputIdsToMove = afterMilestoneId && feed.kind === 'input'
    ? idsAfterMilestone(inputStack, afterMilestoneId, true)
    : new Set((feed.inputMilestones || []).map((m) => m.id));

  const moveOutputToNew = feed.kind === 'output' && (
    placement === 'before'
    || (placement === 'after' && laterOutputIds.size > 0)
  );
  const moveInputToNew = feed.kind === 'input' && placement === 'after';
  const insertBeforeHost = feed.kind === 'input' && placement === 'after' && Boolean(afterMilestoneId);
  const shouldRewire = !insertBeforeHost && (feed.kind === 'input' || placement === 'before' || laterOutputIds.size > 0);
  const rewireBranch = feed.kind === 'input' ? feed.branch : branch;
  const outputIds = placement === 'before'
    ? new Set(outputStack.map((m) => m.id))
    : laterOutputIds;

  return tasks.map((task) => {
    if (task.id === newTaskId) return task;

    let next = task;

    if (moveOutputToNew && isMilestoneNode(next) && outputIds.has(next.id)) {
      next = { ...next, milestoneAnchor: { hostTaskId: newTaskId, side: 'output' } };
    }

    if (moveInputToNew && isMilestoneNode(next) && inputIdsToMove.has(next.id)) {
      next = { ...next, milestoneAnchor: { hostTaskId: newTaskId, side: 'input' } };
    }

    if (insertBeforeHost && next.id === feed.childId) {
      return setTaskDependsOnNew(next, newTaskId);
    }

    if (shouldRewire && !isMilestoneNode(next)) {
      if (feed.kind === 'input' && next.id !== feed.childId) return next;
      next = rewireTaskDependency(next, sourceTaskId, newTaskId, rewireBranch);
    }

    return next;
  });
}
